import { $, _, SVG, tinycolor } from "js/vendor";
import * as d3 from "d3";
import log from "js/core/logger";
import { Palette } from "js/core/models/palette";
import { app } from "js/namespaces";
import { BaseElement } from "js/elements/base/BaseElement";
import imageFilters from "js/core/utilities/imageFilters";
import { HiliteType, PaintStyle, PREDEFINED_PALETTES } from "common/constants";

let filtersNode;

export function parseStylesSheetValue(val) {
    if (val.has("%") || (val.has(" ") && isNaN(parseFloat(val)))) {
        return val;
    } else if (val == "true") {
        return true;
    } else if (val == "false") {
        return false;
    } else if (isNaN(parseFloat(val))) {
        return val;
    } else {
        if (val.indexOf(" + ") > -1 || val.indexOf(" - ") > -1 || val.indexOf(" * ") > -1 || val.indexOf(" / ") > -1) {
            return parseFloat(eval(val));
        } else {
            return parseFloat(val);
        }
    }
}

class StyleSheet {
    constructor(theme, str) {
        this.palette = new Palette(theme);

        // strip comments
        str = str.replace(/\/\*[\s\S]*?\*\/|([^\\:]|^)\/\/.*$/gm, "");

        // find variables
        var variables = [];
        let m;
        var regex = /(\$.*?):(.*?);/g;
        while ((m = regex.exec(str)) !== null) {
            if (m.index === regex.lastIndex) {
                regex.lastIndex++;
            }
            variables.push({
                m: m[0],
                name: m[1].trim(),
                value: m[2].trim()
            });
        }

        this.variables = {};

        // update variables with theme settings

        // load predefined palette colors
        if (!theme.has("colors")) {
            let predefinedPalette = _.find(PREDEFINED_PALETTES, { name: theme.get("palette_name") || "colorful" });
            if (predefinedPalette) {
                theme.set("colors", predefinedPalette.colors);
            }
        }

        // inject styleguide properties into stylesheet variables
        for (let variable of variables) {
            if (theme.has(variable.name.substr(1))) {
                //console.log("Theme variable " + variable.name + "=" + theme.get(variable.name.substr(1)));
                // if the theme has overridden this variable, use it's value to override the stylesheet default value
                variable.value = theme.get(variable.name.substr(1));
            }
            this.variables[variable.name.substr(1)] = this.parseValue(variable.value);
        }

        // create variables for all the theme colors
        _.each(theme.get("colors"), (value, key) => {
            this.variables["color_" + key] = value;
        });

        // set shape variables based on theme style settings
        if (theme.has("styleShape")) {
            switch (theme.get("styleShape")) {
                case "rounded_rect":
                    this.variables["shapeType"] = "rect";
                    break;
                case "rect":
                default:
                    this.variables["shapeType"] = theme.get("styleShape");
                    this.variables["shapeCornerRadius"] = 0;
            }
        }

        this.parseStyleSheet(this, str.trim());
    }

    applyStyleSheetVariables(variables, styleSheet = this, node = this) {
        // apply variables to variable values first
        for (let variable of Object.keys(variables)) {
            let value = variables[variable];
            if (value && typeof (value) == "string" && value.startsWith("$")) {
                variables[variable] = variables[value.substring(1)];
            }
        }

        styleSheet.decorationVariables = _.pick(variables, ["shapeFillColor", "shapeStrokeColor", "shapeCornerRadius", "shapeStrokeWidth", "shapeEffect", "iconStyle", "iconShapeType", "iconFrameFillColor", "iconFrameStrokeColor", "iconFrameStrokeWidth", "iconFrameCornerRadius", "iconFillColor", "connectorStrokeWidth", "connectorDashArray", "primaryConnectorStrokeWidth", "primaryConnectorStrokeColor", "radialBarCircleStrokeWidth", "radialBarCircleStrokeColor", "radialBarCircleDash", "radialBarPathStrokeWidth", "radialBarPathStrokeColor", "radialBarPathDash", "emphasizedFillColor", "emphasizedStrokeColor", "emphasizedStrokeWidth", "deemphasizedFillColor", "deemphasizedStrokeColor", "deemphasizedStrokeWidth", "chartLineWidth", "chartMarkerRadius"]);
        let activeVariables = _.omit(variables, ["shapeFillColor", "shapeStrokeColor", "shapeCornerRadius", "shapeStrokeWidth", "shapeEffect", "iconStyle", "iconShapeType", "iconFrameFillColor", "iconFrameStrokeColor", "iconFrameStrokeWidth", "iconFrameCornerRadius", "iconFillColor", "connectorStrokeWidth", "connectorDashArray", "primaryConnectorStrokeWidth", "primaryConnectorStrokeColor", "radialBarCircleStrokeWidth", "radialBarCircleStrokeColor", "radialBarCircleDash", "radialBarPathStrokeWidth", "radialBarPathStrokeColor", "radialBarPathDash", "emphasizedFillColor", "emphasizedStrokeColor", "emphasizedStrokeWidth", "deemphasizedFillColor", "deemphasizedStrokeColor", "deemphasizedStrokeWidth", "chartLineWidth", "chartMarkerRadius"]);

        for (var key of Object.keys(node)) {
            if (typeof (node[key]) == "object" && key != "palette" && key != "variables") {
                this.applyStyleSheetVariables(variables, styleSheet, node[key]);
            } else {
                if (typeof node[key] === "string") {
                    if (key == "fontStyle") {
                        let fontName = node[key];
                        let isAlwaysEmphasized = false;
                        if (fontName.indexOf("Emphasized") > -1) {
                            fontName = fontName.split("Emphasized")[0];
                            isAlwaysEmphasized = true;
                        }
                        node["font"] == undefined && (node["font"] = activeVariables[fontName + (isAlwaysEmphasized ? "FontEmphasized" : "FontRegular")]);
                        node["emphasizedFont"] == undefined && (node["emphasizedFont"] = activeVariables[fontName + "FontEmphasized"]);
                        node["italicFont"] == undefined && (node["italicFont"] = activeVariables[fontName + (isAlwaysEmphasized ? "FontEmphasizedItalic" : "FontItalic")]);
                        node["emphasizedItalicFont"] == undefined && (node["emphasizedItalicFont"] = activeVariables[fontName + "FontEmphasizedItalic"]);
                        node["lineHeight"] == undefined && (node["lineHeight"] = activeVariables[fontName + "FontLineHeight"]);
                        node["letterSpacing"] == undefined && (node["letterSpacing"] = activeVariables[fontName + "FontLetterSpacing"] || 0);
                        node["textTransform"] == undefined && (node["textTransform"] = activeVariables[fontName + "FontTextTransform"] || "auto");
                        node["fontScaling"] == undefined && (node["fontScaling"] = activeVariables[fontName + "FontScaling"] || 100);
                        node["filter"] == undefined && (node["filter"] = activeVariables[fontName + "FontEffect"] || "none");
                        if (node["bold"] == undefined) {
                            node["bold"] = {};
                        }
                        (node["bold"]["fontColor"] == undefined && activeVariables[fontName + "FontBoldColor"] != undefined) && (node["bold"]["fontColor"] = activeVariables[fontName + "FontBoldColor"]);
                    }

                    node[key] = node[key].replace(/(\$.*?)(?= |'|"|;|$|})/g, $.proxy(function(match, p1) {
                        if (activeVariables[p1.substr(1)]) {
                            return activeVariables[p1.substr(1)];
                        } else {
                            return p1;
                        }
                    }, styleSheet));

                    node[key] = this.parseValue(node[key]);
                }
            }
        }
    }

    // ppt exporter doesn't support alpha in a fillColor or strokeColor so we are pre-processing to split out those colors into strokeColor/strokeOpacity fillColor/fillOpacity
    processFillAndStroke(node = this) {
        for (let key of Object.keys(node)) {
            if (typeof (node[key]) == "object" && key != "palette" && key != "variables") {
                this.processFillAndStroke(node[key]);
            } else {
                if (key == "fillColor" || key == "strokeColor") {
                    let value = node[key];
                    if (value.contains(" ") && !isNaN(value.split(" ")[1])) {
                        node[key] = value.split(" ")[0];
                        node[key.replace("Color", "Opacity")] = parseFloat(value.split(" ")[1]);
                    } else if (node[key.replace("Color", "Opacity")] == undefined) {
                        node[key.replace("Color", "Opacity")] = 1;
                    }
                }
            }
        }
    }

    parseValue(val) {
        if (val.has("%") || (val.has(" ") && isNaN(parseFloat(val)))) {
            return val;
        } else if (val == "true") {
            return true;
        } else if (val == "false") {
            return false;
        } else if (isNaN(parseFloat(val))) {
            return val;
        } else {
            if (val.indexOf(" + ") > -1 || val.indexOf(" - ") > -1 || val.indexOf(" * ") > -1 || val.indexOf(" / ") > -1) {
                return parseFloat(eval(val));
            } else {
                return parseFloat(val);
            }
        }
    }

    parseStyleSheet(node, str) {
        var selector;
        var rules;
        var property;
        var value;
        var pos = 0;
        var chunk = "";
        var indent = 0;

        var inString = false;

        while (pos < str.length) {
            var ch = str[pos];
            chunk += ch;
            pos++;

            if (!inString && ch == "{" && indent == 0) {
                selector = chunk.replace("{", "").trim();
                node[selector] = {};
                chunk = "";
                indent = 1;
            } else if (!inString && ch == "{") {
                indent++;
            } else if (!inString && ch == "}") {
                indent--;
                if (indent == 0) {
                    rules = chunk.substr(1);
                    chunk = "";
                    this.parseStyleSheet(node[selector], rules);
                }
            } else if (!inString && ch == ":" && indent == 0) {
                property = chunk.replace(":", "").trim();
                if (property.has("-")) {
                    property = property.split("-")[0] + property.split("-")[1].toTitleCase();
                }

                chunk = "";
            } else if (!inString && ch == ";" && indent == 0) {
                value = chunk.replace(/;$/, "").replace(/"||'/g, "").trim();
                chunk = "";

                switch (property) {
                    case "margin":
                        var margins = value.split(" ");
                        switch (margins.length) {
                            case 1:
                                node.marginLeft = node.marginRight = node.marginTop = node.marginBottom = parseFloat(value);
                                break;
                            case 2:
                                node.marginTop = node.marginBottom = parseFloat(margins[0]);
                                node.marginLeft = node.marginRight = parseFloat(margins[1]);
                                break;
                            case 3:
                                node.marginTop = parseFloat(margins[0]);
                                node.marginLeft = node.marginRight = parseFloat(margins[1]);
                                node.marginBottom = parseFloat(margins[2]);
                                break;
                            default:
                                node.marginTop = parseFloat(margins[0]);
                                node.marginRight = parseFloat(margins[1]);
                                node.marginBottom = parseFloat(margins[2]);
                                node.marginLeft = parseFloat(margins[3]);
                        }
                        break;
                    case "padding":
                        var padding = value.split(" ");
                        switch (padding.length) {
                            case 1:
                                node.paddingLeft = node.paddingRight = node.paddingTop = node.paddingBottom = parseFloat(value);
                                break;
                            case 2:
                                node.paddingTop = node.paddingBottom = parseFloat(padding[0]);
                                node.paddingLeft = node.paddingRight = parseFloat(padding[1]);
                                break;
                            case 3:
                                node.paddingTop = parseFloat(padding[0]);
                                node.paddingLeft = node.paddingRight = parseFloat(padding[1]);
                                node.paddingBottom = parseFloat(padding[2]);
                                break;
                            default:
                                node.paddingTop = parseFloat(padding[0]);
                                node.paddingRight = parseFloat(padding[1]);
                                node.paddingBottom = parseFloat(padding[2]);
                                node.paddingLeft = parseFloat(padding[3]);
                        }
                        break;
                    case "fontSize":
                        node[property] = this.parseValue(value);
                        break;
                    case "strokeDash":
                        node[property] = value;
                        break;
                    default:
                        node[property] = this.parseValue(value);
                }
            } else if (ch == "\"") {
                inString = !inString;
            }
        }
        // create margins and padding objects
        if (node) {
            Object.defineProperty(node, "margins", {
                get() {
                    return {
                        left: node.marginLeft || 0,
                        right: node.marginRight || 0,
                        top: node.marginTop || 0,
                        bottom: node.marginBottom || 0
                    };
                }
            });
            Object.defineProperty(node, "padding", {
                get() {
                    return {
                        left: node.paddingLeft || 0,
                        right: node.paddingRight || 0,
                        top: node.paddingTop || 0,
                        bottom: node.paddingBottom || 0
                    };
                }
            });
        }
        return node;
    }

    getStyleNode(path, leafElement) {
        if (typeof path == "string") {
            path = path.split("/");
        }
        // console.log("search for styles at " + path.join("/"));

        // if a leafElement isn't provided we will always return the styles at the provided path
        var foundStylesForElement = leafElement == null;

        var node = this; // start searching at the root style node

        // move through the chunks of the path, checking to see if a style node exists for that chunk
        // if a leafElement was provided, we will only return a style node for that matches that leafElement - there is no inheritance from parent style nodes
        for (var i of path) {
            if (i instanceof BaseElement) {
                var foundNode = null;
                if (node[i.type + "." + i.id]) {
                    foundNode = node[i.type + "." + i.id];
                } else if (node[i.id]) {
                    foundNode = node[i.id];
                } else if (node[i.type]) {
                    foundNode = node[i.type];
                }

                if (foundNode) {
                    node = foundNode;
                    if (i == leafElement) {
                        foundStylesForElement = true;
                    }
                } else {
                    // we will continue past missing intermediate nodes if we didn't find a match in path
                    // but we don't continue if we didn't find the root of the path
                    if (i == path[0]) {
                        break;
                    }
                }
            } else {
                if (node[i]) {
                    node = node[i];
                }
            }
        }

        if (!leafElement || foundStylesForElement) {
            //             if (Object.keys(node).length) {
            //                console.log("found styles");
            //                console.log(node);
            //             }
            return _.cloneDeep(node);
        } else {
            return null;
        }
    }

    // utility function to convert BSS properties into SVG style properties
    static ProcessStyleSheet(styleNode, slideColor, backgroundColor, options = { itemIndex: 0 }) {
        var styles = {};
        styles.filter = "";
        styles.mask = "";
        _.each(styleNode, (value, property) => {
            if (typeof value === "string" && value.startsWith("$")) {
                value = app.currentTheme.styleSheet.decorationVariables[value.substring(1)];
            }

            switch (property) {
                case "stroke":
                case "strokeColor":
                    var color = app.currentTheme.palette.getForeColor(value, slideColor, backgroundColor, options);
                    styles["stroke"] = color.toRgbString();
                    // styles["stroke-opacity"] = color._a;
                    break;
                case "strokeWidth":
                    styles["stroke-width"] = value;
                    break;
                case "fill":
                case "fillColor":
                case "fontColor":
                    if (typeof value === "string" && (value.startsWith("'gradient") || value.startsWith("'pattern"))) {
                        styles["fill"] = value;
                    } else {
                        styles["fill"] = app.currentTheme.palette.getForeColor(value, slideColor, backgroundColor, options).toRgbString();
                    }
                    break;
                case "fillOpacity":
                    styles["fill-opacity"] = value;
                    break;
                case "strokeOpacity":
                    styles["stroke-opacity"] = value;
                    break;
                case "strokeDash":
                    styles["stroke-dasharray"] = value;
                    break;
                case "filter":
                    if (styleNode.filter != null && styleNode.filter != "none" && !window.hideFilters) {
                        let filter = styleNode.filter;
                        if (filter.indexOf("[index]")) {
                            filter = filter.replace("[index]", options.itemIndex % 4 + 1);
                        }

                        let filterId = filter;

                        switch (filter) {
                            case "shadowGlow":
                                filterId = createShadowGlowFilter(styleNode, slideColor, backgroundColor);
                                break;
                            case "textStroke":
                                filterId = createTextStrokeFilter(styleNode, slideColor, backgroundColor);
                                break;
                        }
                        styles["filter"] = `url(#${filterId})`;
                    }
                    break;
                case "mask":
                    if (styleNode.mask != "none") {
                        styles["mask"] = `url(#${styleNode.mask})`;
                    }
                    break;
                case "pattern":
                    if (styleNode.pattern != "none") {
                        styles["pattern"] = `url(#${styleNode.pattern})`;
                    }
                    break;
                default:
                    styles[property] = value;
                    break;
            }
        });
        return styles;
    }

    createFilters() {
        $("#svg-filters").remove();
        filtersNode = $("body").addEl($.div("filters").attr("id", "svg-filters"))[0];
        if (this.Filters) {
            _.each(this.Filters, filter => {
                SVG(filtersNode).svg(`<defs>${filter.def}</defs>`);
            });

            _.forIn(imageFilters, filter => {
                SVG(filtersNode).svg(filter);
            });
        }
    }

    applyHiliteStyles(styles, hiliteState) {
        switch (hiliteState) {
            case HiliteType.DEEMPHASIZED:
                if (styles.deemphasized) {
                    return _.merge(styles, styles.deemphasized);
                } else {
                    return styles;
                }
            case HiliteType.EMPHASIZED:
                if (styles.emphasized) {
                    return _.merge(styles, styles.emphasized);
                } else {
                    return styles;
                }
            case HiliteType.NONE:
                return styles;
        }
        return styles;
    }
}

$.fn.getStyleSheet = function(styleNode, slideColor, backgroundColor, options) {
    return StyleSheet.ProcessStyleSheet(styleNode, slideColor, backgroundColor, options);
};
//
// $.fn.applyStyleSheet = function (styleNode, backgroundColor, transitionColors) {
//     $(this).css(StyleSheet.ProcessStyleSheet(styleNode, backgroundColor));
// };

SVG.Element.prototype.applyStyleSheet = function(styleNode, slideColor, backgroundColor, options = { itemIndex: 0 }) {
    let styles = StyleSheet.ProcessStyleSheet(styleNode, slideColor, backgroundColor, options);

    if (styles.fill && styles.fill.startsWith("'gradient")) {
        try {
            let gradientDef = JSON.parse(styles.fill.substring("'gradient".length, styles.fill.length - 1));
            let gradient;

            switch (gradientDef.type) {
                case "linear":

                    gradient = this.doc().gradient("linear", stop => {
                        for (let stopDef of gradientDef.stops) {
                            stop.at(stopDef.stop, app.currentTheme.palette.getForeColor(stopDef.color, slideColor, backgroundColor, options).toHexString());
                        }
                    });

                    switch (styles.gradientDirection || "top-bottom") {
                        case "top-bottom":
                            gradient.from(0, 0).to(0, 1);
                            break;
                        case "bottom-top":
                            gradient.from(0, 1).to(0, 0);
                            break;
                        case "left-right":
                            gradient.from(0, 0).to(1, 0);
                            break;
                        case "right-left":
                            gradient.from(1, 0).to(0, 0);
                            break;
                    }

                    styles.fill = gradient;
                    break;
                case "radial":

                    gradient = this.doc().gradient("radial", stop => {
                        for (let stopDef of gradientDef.stops) {
                            stop.at(stopDef.stop, app.currentTheme.palette.getForeColor(stopDef.color, slideColor, backgroundColor, options).toHexString());
                        }
                    });
                    gradient.attr("cx", gradientDef.cx || "50%").attr("cy", gradientDef.cy || "50%");
                    styles.fill = gradient;

                    break;
            }
        } catch (ex) {
            log.error("UNABLE TO PARSE GRADIENT");
            styles.fill = app.currentTheme.palette.getForeColor("slide", slideColor, backgroundColor, options);
        }
    } else if (styles.fill && styles.fill.startsWith("'pattern")) {
        try {
            let patternDef = JSON.parse(styles.fill.substring("'pattern".length, styles.fill.length - 1));
            switch (patternDef.type) {
                case "image": {
                    let maskImage = this.doc().image(patternDef.url).move(-20, -20);
                    let mask = this.doc().mask().add(maskImage);
                    this.maskWith(mask);
                    styles.fill = app.currentTheme.palette.getForeColor("slide", slideColor, backgroundColor, options);
                    break;
                }
            }
        } catch (ex) {
            log.error("UNABLE TO PARSE GRADIENT");
            styles.fill = app.currentTheme.palette.getForeColor("slide", slideColor, backgroundColor, options);
        }
    }

    if (options.animate && parseInt(this.width()) > 0 && parseInt(this.height()) > 0) {
        if (styles.fill) {
            this.applyPaletteColor(styles.fill, PaintStyle.FILL, true);
            _.unset(styles, "fill");
        }
        if (styles.stroke) {
            this.applyPaletteColor(styles.stroke, PaintStyle.STROKE, true);
            _.unset(styles, "stroke");
        }
    }

    // convert stroke colors with alpha to use stroke-opacity style for PPT export support
    if (styles.stroke) {
        let strokeColor = tinycolor(styles.stroke);
        if (strokeColor.getAlpha() != 1) {
            styles["stroke-opacity"] = strokeColor.getAlpha() * (styles["stroke-opacity"] || 1);
            styles.stroke = strokeColor.setAlpha(1).toRgbString();
        }
    }

    this.style(styles);

    if (this instanceof SVG.Rect && styles.cornerRadius) {
        this.radius(styles.cornerRadius);
    }

    return styles;
};

SVG.Set.prototype.applyStyleSheet = function(styleNode, slideColor, backgroundColor, transitionColors) {
    for (let element of this.members) {
        element.applyStyleSheet(styleNode, slideColor, backgroundColor, transitionColors);
    }
};

d3.selection.prototype.applyStyleSheet = function(styleNode, slideColor, backgroundColor, transitionColors) {
    _.each(StyleSheet.ProcessStyleSheet(styleNode, slideColor, backgroundColor), (value, property) => {
        this.style(property, value);
    });
};

class ElementStyles {
    constructor() {
        this.font = "Source Sans Pro";
        this.fontWeight = 500;
        this.fontSize = 20;

        this.marginLeft = 0;
        this.marginRight = 0;
        this.marginTop = 0;
        this.marginBottom = 0;
        this.paddingLeft = 0;
        this.paddingRight = 0;
        this.paddingTop = 0;
        this.paddingBottom = 0;
    }

    get margins() {
        return {
            left: this.marginLeft,
            right: this.marginRight,
            top: this.marginTop,
            bottom: this.marginBottom
        };
    }

    get padding() {
        return {
            left: this.paddingLeft,
            right: this.paddingRight,
            top: this.paddingTop,
            bottom: this.paddingBottom
        };
    }

    replaceVariable(variable, value) {
        let stylesAsJSON = JSON.stringify(this);
        // Javascript does nor allow shorthand regex for global replace
        // based on a variable pattern -- need to instantiate an actual
        // instance of the RegExp object in order to do so.
        const variableReplacer = new RegExp(variable, "g");
        stylesAsJSON = stylesAsJSON.replace(variableReplacer, value);
        _.extend(this, JSON.parse(stylesAsJSON));
    }
}

function createShadowGlowFilter(styleNode, slideColor, backgroundColoryles) {
    let filterId = "shadowGlow_" + styleNode.fontSize;

    if ($("#svg-filters").find("#" + filterId).length == 0) {
        let filterSVG = `<filter id='${filterId}' x='-25%' y='-25%' width='200%' height='200%'>
             <feGaussianBlur in='SourceAlpha' result='blur-out' stdDeviation='${styleNode.fontSize / 7}' />
             <feColorMatrix in='blur-out' result='color-out' type='matrix'
              values='0 0 0 0   0
                      0 0 0 0   0
                      0 0 0 0   0
                      0 0 0 .5 0'/>
             <feBlend in='SourceGraphic' in2='color-out' mode='normal'/>
          </filter>`;

        SVG(filtersNode).svg(`<defs>${filterSVG}</defs>`);
    }
    return filterId;
}

function createTextStrokeFilter(styleNode, slideColor, backgroundColor) {
    let filterId = "textStroke_" + backgroundColor.toHexString();

    if ($("#svg-filters").find("#" + filterId).length == 0) {
        let filterSVG = `<filter id='${filterId}' x='-50%' y='-50%' width='200%' height='200%'>
            <feMorphology in='SourceAlpha' result='DILATED' operator='dilate' radius='4 2'></feMorphology>

            <feFlood flood-color='${backgroundColor.toHexString()}' flood-opacity='1' result='WHITE'></feFlood>
            <feComposite in='WHITE' in2='DILATED' operator='in' result='OUTLINE'></feComposite>

            <feMerge>
                <feMergeNode in='OUTLINE'/>
                <feMergeNode in='SourceGraphic'/>
            </feMerge>
          </filter>`;

        SVG(filtersNode).svg(`<defs>${filterSVG}</defs>`);
    }
    return filterId;
}

export { StyleSheet, ElementStyles };
